import { Injectable, signal, computed } from '@angular/core';
import { ApiService } from './api.service';
import { catchError, finalize, tap } from 'rxjs/operators';
import { Observable, of, Subject } from 'rxjs';
import { IPaymentLockResponse } from '../models/Payment';
import { AuthService } from '../abstraction/auth.service';
import { IDataWidget } from '../models/WidgetUser';

@Injectable({
  providedIn: 'root',
})
export class QrPaymentService {
  // Payment constants
  readonly DEPOSIT_ADDRESS = '0x25203459520B983b854d643eC4fCf1c0583d6Ae0';
  readonly AGAINST_CURRENCY = 'ARS';

  // QR 3.0 Payment Data
  qrRawData: string | null = null;
  merchantName: string | null = null;
  merchantCity: string | null = null;
  transactionAmount: string | null = null;
  transactionCurrency: string | null = null;
  merchantAccount: string | null = null;
  merchantCategoryCode: string | null = null;
  countryCode: string | null = null;
  postalCode: string | null = null;
  additionalData: string | null = null;
  qrNeedsAmount = signal<boolean>(false);

  // User-entered payment amount
  paymentAmount = signal<number | null>(null);

  // Loading state for payment lock
  isLoadingLock = signal<boolean>(false);

  // Payment recipient name (merchant name) from lock response
  paymentRecipientName = signal<string | null>(null);

  // Payment address (wallet address) from lock response
  paymentAddress = signal<string | null>(null);

  // Lock code returned from backend when locking a payment
  lockCode: string | null = null;

  // Payment redirect data (from lock response)
  paymentRedirectData: {
    amount?: string;
    coin?: string;
    fiatAmount?: string;
    depositAddress?: string;
    against?: string;
    exchange?: string;
  } | null = null;

  // Signal to track if payment is locked (has lockCode)
  isPaymentLocked = signal<boolean>(false);

  // Subject to emit lock responses for UI logging
  private lockResponseSubject = new Subject<{ type: 'success' | 'error'; data: any }>();
  lockResponse$ = this.lockResponseSubject.asObservable();

  // Computed signal: payment is locked and has amount (ready to create)
  isReadyToCreate = computed(() => this.isPaymentLocked() && !!this.paymentAmount());

  // Computed signal: should show amount input as disabled (locked with amount)
  shouldDisableAmountInput = computed(() => this.isPaymentLocked() && !!this.paymentAmount());

  constructor(private apiService: ApiService, private authSvc: AuthService) {}

  /**
   * Parse QR 3.0 payment data (EMVCo format) and set service variables
   * Format: ID(2) + Length(2) + Value(variable)
   */
  parseAndSetQrData(qrData: string): void {
    try {
      // Reset all fields
      this.qrRawData = qrData;
      this.merchantName = null;
      this.merchantCity = null;
      this.transactionAmount = null;
      this.transactionCurrency = null;
      this.merchantAccount = null;
      this.merchantCategoryCode = null;
      this.countryCode = null;
      this.postalCode = null;
      this.additionalData = null;

      let index = 0;

      while (index < qrData.length) {
        if (index + 4 > qrData.length) break;

        const id = qrData.substring(index, index + 2);
        const length = parseInt(qrData.substring(index + 2, index + 4), 10);

        if (isNaN(length) || index + 4 + length > qrData.length) break;

        const value = qrData.substring(index + 4, index + 4 + length);

        // Parse known QR 3.0 fields
        switch (id) {
          case '00': // Payload Format Indicator
            // Usually "01" for QR Code
            break;
          case '26': // Merchant Account Information (can be 26-51)
          case '27':
          case '28':
          case '29':
          case '30':
          case '31':
          case '32':
          case '33':
          case '34':
          case '35':
          case '36':
          case '37':
          case '38':
          case '39':
          case '40':
          case '41':
          case '42':
          case '43':
          case '44':
          case '45':
          case '46':
          case '47':
          case '48':
          case '49':
          case '50':
          case '51':
            // Parse nested merchant account info (usually starts with 00 for GUID)
            if (!this.merchantAccount && value.length > 2) {
              const guid = value.substring(0, 2);
              if (guid === '00') {
                const guidLength = parseInt(value.substring(2, 4), 10);
                if (!isNaN(guidLength)) {
                  this.merchantAccount = value.substring(4, 4 + guidLength);
                }
              }
            }
            break;
          case '52': // Merchant Category Code
            this.merchantCategoryCode = value;
            break;
          case '53': // Transaction Currency
            this.transactionCurrency = value;
            break;
          case '54': // Transaction Amount
            this.transactionAmount = value;
            break;
          case '58': // Country Code
            this.countryCode = value;
            break;
          case '59': // Merchant Name
            this.merchantName = value;
            break;
          case '60': // Merchant City
            this.merchantCity = value;
            break;
          case '61': // Postal Code
            this.postalCode = value;
            break;
          case '62': // Additional Data Field Template
            this.additionalData = value;
            break;
          case '63': // CRC
            // Checksum, skip
            break;
        }

        index += 4 + length;
      }

      // After successful parsing, call the payment lock endpoint
      this.lockPayment().subscribe({
        next: (response) => {
          // Response is already emitted via lockResponse$ observable in the pipe
          this.handleLockResponse(response);
        },
      });
    } catch (error) {
      console.error('Error parsing QR 3.0 data:', error);
      // If parsing fails, at least store the raw data
      this.qrRawData = qrData;
    }
  }

  getPaymentStatus(signature: string) {
    const obs = this.apiService.getPaymentStatus(signature);
    obs.subscribe((resp) => {
      this.authSvc.setStatusWidgetUser(resp.data as IDataWidget);
    });
    return obs;
  }

  /**
   * Lock payment by calling the payment lock endpoint
   * Need to do this twice to get amount or know to ask for amount
   */
  lockPayment(): Observable<IPaymentLockResponse | null> {
    if (!this.qrRawData) {
      console.warn('No QR raw data available to lock payment');
      return of(null);
    }

    this.isLoadingLock.set(true);
    const amount = this.paymentAmount(); // starts at null
    const signature = this.authSvc.onboardingState.value.signature;
    const lockObservable = amount
      ? this.apiService.lockPayment(this.qrRawData, amount, signature)
      : this.apiService.lockPayment(this.qrRawData, null, signature);

    return lockObservable.pipe(
      tap((response) => {
        // Emit success response for UI logging
        if (response) {
          this.lockResponseSubject.next({ type: 'success', data: response });
        } else {
          this.lockResponseSubject.next({
            type: 'error',
            data: { message: 'Null response received' },
          });
        }
      }),
      catchError((error) => {
        console.error('Error locking payment:', error);
        // Emit error for UI logging
        this.lockResponseSubject.next({ type: 'error', data: error });
        return of(null);
      }),
      finalize(() => {
        this.isLoadingLock.set(false);
      }),
    );
  }

  /**
   * Handle lock payment response and update state
   */
  handleLockResponse(response: IPaymentLockResponse | null): void {
    if (response && response.data) {
      // Extract paymentRecipientName and paymentAssetAmount from response data
      const paymentRecipientName = response.data.paymentRecipientName?.toUpperCase();
      const paymentAssetAmount = response.data.paymentAssetAmount;
      const paymentAddress = response.data.user.externalId; // externalId is address

      // Store payment recipient name (merchant name) from first lock
      if (paymentRecipientName) {
        this.paymentRecipientName.set(paymentRecipientName);
        this.paymentAddress.set(paymentAddress);
      }

      if (paymentAssetAmount) {
        // paymentAssetAmount from API is in base units (e.g., "20" = 20 ARS)
        // Store it as-is since the component now works directly with base units
        const amount = parseFloat(paymentAssetAmount);
        if (!isNaN(amount) && amount > 0) {
          this.paymentAmount.set(amount);
        }
      }
      // Payment is considered locked if we have payment data
      this.isPaymentLocked.set(true);
      this.qrNeedsAmount.set(false);

      // Store payment redirect data from response.data
      const depositAddress =
        response.data.user?.addresses?.depositAddresses?.WORLDCHAIN ||
        response.data.user?.addresses?.depositAddresses?.ETHEREUM ||
        this.DEPOSIT_ADDRESS;

      this.paymentRedirectData = {
        amount: response.data.paymentAssetAmount,
        coin: response.data.paymentAsset,
        fiatAmount: response.data.paymentAgainstAmount,
        depositAddress: depositAddress,
        against: response.data.paymentAgainst,
        exchange: response.data.exchange,
      };

      // Store lockCode if present
      if (response.data.lockCode) {
        this.lockCode = response.data.lockCode;
      }
    } else {
      // If no response or no data, check if we need user input
      if (!this.paymentAmount()) {
        this.qrNeedsAmount.set(true);
      }
    }
  }

  /**
   * Set the payment amount entered by the user
   */
  setPaymentAmount(amount: number | null): void {
    this.paymentAmount.set(amount);
  }

  /**
   * Get the payment amount
   */
  getPaymentAmount(): number | null {
    return this.paymentAmount();
  }

  /**
   * Create payment by calling the payment create endpoint
   * Handles loading and errors internally for simplicity
   */
  createPayment(txId?: string): Observable<any> {
    if (!this.isPaymentLocked() && !txId) {
      console.error('Payment is not locked, cannot create payment');
      return of(null);
    }

    this.isLoadingLock.set(true);

    const signature = this.authSvc.onboardingState.value.signature;
    return this.apiService.createPayment(txId, signature).pipe(
      catchError((error) => {
        console.error('Error creating payment:', error);
        return of(null);
      }),
      finalize(() => {
        this.isLoadingLock.set(false);
      }),
    );
  }

  /**
   * Clear all QR payment data
   */
  clearQrData(): void {
    this.qrRawData = null;
    this.merchantName = null;
    this.merchantCity = null;
    this.transactionAmount = null;
    this.transactionCurrency = null;
    this.merchantAccount = null;
    this.merchantCategoryCode = null;
    this.countryCode = null;
    this.postalCode = null;
    this.additionalData = null;
    this.paymentAmount.set(null);
    this.isLoadingLock.set(false);
    this.paymentRecipientName.set(null);
    this.isPaymentLocked.set(false);
    this.qrNeedsAmount.set(false);
    this.paymentRedirectData = null;
  }
}
